// src/scripts/useScripts/useVenueContext.js
import {
  ref,
  watch,
  computed,
} from "vue";

import {
  auth,
  authReady,
  db,
} from "@/firebase";

import {
  onAuthStateChanged,
} from "firebase/auth";

import {
  doc,
  onSnapshot,
} from "firebase/firestore";

import {
  readAuthenticatedUserProfile,
} from "@/scripts/userContext";

const LS_VENUE_A =
  "coredeck_venue";

const LS_VENUE_B =
  "venue"; // legacy

// ✅ Add week ending storage keys (non-breaking)
const LS_WEEK_ENDING_A =
  "weekEndingDate";

const LS_WEEK_ENDING_B =
  "weekEnding"; // legacy

const venueId =
  ref(
    localStorage.getItem(
      LS_VENUE_A
    ) ||
      localStorage.getItem(
        LS_VENUE_B
      ) ||
      ""
  );

const tradingDate =
  ref("");

// Base week ending from Firestore day_state (SSoT)
const weekEndingBase =
  ref(
    localStorage.getItem(
      LS_WEEK_ENDING_A
    ) ||
      localStorage.getItem(
        LS_WEEK_ENDING_B
      ) ||
      ""
  );

// ✅ NEW: Optional "view week" override (e.g. dashboard week scroller)
const viewWeekEndingOverride =
  ref("");

const venueReady =
  ref(false);

/**
 * ✅ NEW: Many views/components expect `userLoaded.value` from venue context.
 * Keep this as a stable ref to avoid undefined.value crashes during venue switching.
 */
const userLoaded =
  ref(false);

const isOperations =
  ref(false);

// STRICT: Only use venue_ids when user_type === "multi"
const userType =
  ref("single"); // "single" | "multi" (default safe)

const isMultiVenue =
  ref(false);

const allowedVenueIds =
  ref([]); // string[]

// DAY STATE FLAGS (single source of truth)
const isOpen =
  ref(false);

const weekStartPending =
  ref(false);

/**
 * ✅ Banking Summary payload (SSoT for End Week)
 * Shape: { pdq:number[7], cash:number[7], total:number[7] }
 */
const bankingSummaryPayload =
  ref(null);

/* =========================
   DATE GUARD (DEV-TOGGLE)
   =========================
   This file is read-only for tradingDate; it reflects Firestore day_state.
   The guard below is a safety net to prevent the UI from displaying a
   future tradingDate (e.g., if the writer-side guard is disabled).
   To enable it quickly, uncomment the guarded block in startDayState().
*/
function todayISO() {
  const d =
    new Date();

  d.setHours(
    0,
    0,
    0,
    0
  );

  return d
    .toISOString()
    .slice(
      0,
      10
    ); // YYYY-MM-DD
}

function isFutureISO(
  dateStr
) {
  if (!dateStr) {
    return false;
  }

  // Assumes dateStr is "YYYY-MM-DD"
  return (
    String(
      dateStr
    ) >
    todayISO()
  );
}

function setBankingSummaryPayload(
  payload
) {
  // Force a plain JSON-safe object (avoid Proxy/Refs leaking)
  if (
    !payload ||
    typeof payload !==
      "object"
  ) {
    bankingSummaryPayload.value =
      null;

    return;
  }

  const pdq =
    Array.isArray(
      payload.pdq
    )
      ? payload.pdq.map(
          (
            n
          ) =>
            Number(
              n ||
                0
            )
        )
      : null;

  const cash =
    Array.isArray(
      payload.cash
    )
      ? payload.cash.map(
          (
            n
          ) =>
            Number(
              n ||
                0
            )
        )
      : null;

  const total =
    Array.isArray(
      payload.total
    )
      ? payload.total.map(
          (
            n
          ) =>
            Number(
              n ||
                0
            )
        )
      : null;

  if (
    !pdq ||
    !cash ||
    !total ||
    pdq.length !==
      7 ||
    cash.length !==
      7 ||
    total.length !==
      7
  ) {
    bankingSummaryPayload.value =
      null;

    return;
  }

  bankingSummaryPayload.value =
    {
      pdq,
      cash,
      total,
    };
}

function asBool(
  v
) {
  if (
    v ===
    true
  ) {
    return true;
  }

  if (
    v ===
    false
  ) {
    return false;
  }

  if (
    typeof v ===
    "string"
  ) {
    const value =
      v
        .trim()
        .toLowerCase();

    if (
      value ===
      "true"
    ) {
      return true;
    }

    if (
      value ===
      "false"
    ) {
      return false;
    }
  }

  return false;
}

function asString(
  v
) {
  return String(
    v ?? ""
  ).trim();
}

function uniqStrings(
  arr
) {
  const out = [];

  const seen =
    new Set();

  for (
    const v of Array.isArray(
      arr
    )
      ? arr
      : []
  ) {
    const value =
      asString(
        v
      );

    if (!value) {
      continue;
    }

    if (
      seen.has(
        value
      )
    ) {
      continue;
    }

    seen.add(
      value
    );

    out.push(
      value
    );
  }

  return out;
}

function setActiveVenueStorage(
  v
) {
  const val =
    asString(
      v
    );

  if (!val) {
    return;
  }

  localStorage.setItem(
    LS_VENUE_A,
    val
  );

  localStorage.setItem(
    LS_VENUE_B,
    val
  );
}

// ✅ NEW: persist week ending (non-breaking)
function setWeekEndingStorage(
  we
) {
  const val =
    asString(
      we
    );

  if (!val) {
    return;
  }

  localStorage.setItem(
    LS_WEEK_ENDING_A,
    val
  );

  localStorage.setItem(
    LS_WEEK_ENDING_B,
    val
  );
}

/* =========================
   View-week override API
   =========================
   If set, every component using useVenueContext().weekEnding will see the override.
   Use "" or null to clear.
*/
export function setViewWeekEndingOverride(
  isoOrNull
) {
  const v =
    asString(
      isoOrNull
    );

  viewWeekEndingOverride.value =
    v ||
    "";
}

export function clearViewWeekEndingOverride() {
  viewWeekEndingOverride.value =
    "";
}

/* =========================
   Effective weekEnding (override-aware)
   ========================= */
const weekEnding =
  computed(
    () => {
      return (
        viewWeekEndingOverride.value ||
        weekEndingBase.value ||
        ""
      );
    }
  );

/* =========================
   day_state snapshot
   ========================= */
let dayStateUnsub =
  null;

function stopDayState() {
  if (
    dayStateUnsub
  ) {
    dayStateUnsub();

    dayStateUnsub =
      null;
  }
}

function startDayState(
  venueIdValue
) {
  stopDayState();

  const id =
    asString(
      venueIdValue
    );

  if (!id) {
    isOpen.value =
      false;

    weekStartPending.value =
      false;

    tradingDate.value =
      "";

    weekEndingBase.value =
      "";

    bankingSummaryPayload.value =
      null;

    return;
  }

  const refDoc =
    doc(
      db,
      "venues",
      id,
      "day_state",
      "state"
    );

  dayStateUnsub =
    onSnapshot(
      refDoc,

      (
        snap
      ) => {
        const data =
          snap.exists()
            ? snap.data() ||
              {}
            : {};

        isOpen.value =
          data.isOpen ===
          true;

        weekStartPending.value =
          data.weekStartPending ===
          true;

        const td =
          data.tradingDate
            ? String(
                data.tradingDate
              )
            : "";

        const we =
          data.weekEndingDate
            ? String(
                data.weekEndingDate
              )
            : "";

        /* 🚧 DEV GUARD: prevent future trading dates
           Uncomment before go-live
        */
        if (
          td &&
          isFutureISO(
            td
          )
        ) {
          console.warn(
            "[useVenueContext] Future tradingDate blocked:",
            td
          );
        } else if (
          td
        ) {
          tradingDate.value =
            td;
        }

        // ✅ FIX: week ending must be set (this unblocks Submit buttons everywhere)
        if (
          we
        ) {
          weekEndingBase.value =
            we;

          setWeekEndingStorage(
            we
          );
        } else {
          // If missing in Firestore, clear so UI doesn't show stale
          weekEndingBase.value =
            "";
        }

        // // DEV MODE (guard disabled):
        // if (td) tradingDate.value = td;
      },

      (
        err
      ) =>
        console.warn(
          "[useVenueContext] day_state snapshot error:",
          err
        )
    );
}

// dropdown event (ops + multi)
if (
  typeof window !==
  "undefined"
) {
  window.addEventListener(
    "ops-venue-changed",

    (
      e
    ) => {
      const v =
        asString(
          e?.detail ||
            ""
        );

      if (!v) {
        return;
      }

      if (
        !isOperations.value &&
        !isMultiVenue.value
      ) {
        return;
      }

      if (
        isMultiVenue.value &&
        allowedVenueIds.value
          .length
      ) {
        if (
          !allowedVenueIds.value.includes(
            v
          )
        ) {
          return;
        }
      }

      venueId.value =
        v;

      setActiveVenueStorage(
        v
      );

      venueReady.value =
        true;

      startDayState(
        v
      );
    }
  );
}

/**
 * Load the current user's CoreDeck profile.
 *
 * Uses the same guarded profile reader as emailLogin.js,
 * rather than starting an unrelated raw getDoc at the same time.
 */
async function loadUserDoc(
  uid
) {
  const snap =
    await readAuthenticatedUserProfile(
      uid
    );

  if (
    !snap.exists()
  ) {
    return {
      ops:
        false,

      user_type:
        "single",

      venue_id:
        "",

      venue_ids:
        [],
    };
  }

  const data =
    snap.data() ||
    {};

  const ops =
    asBool(
      data.isOperations ??
        data.is_operations ??
        data.operations ??
        false
    );

  const uType =
    asString(
      data.user_type ||
        ""
    )
      .toLowerCase() ||
    "single";

  /*
   * Existing venue-field precedence retained unchanged.
   */
  const vId =
    asString(
      data.venue_id ||
        data.venueId ||
        data.activeVenueId ||
        data.venue ||
        ""
    );

  const vIds =
    uniqStrings(
      data.venue_ids ||
        data.venueIds ||
        []
    );

  return {
    ops,

    user_type:
      uType,

    venue_id:
      vId,

    venue_ids:
      vIds,
  };
}

async function syncUser(
  user
) {
  // user is present, but the profile load is in-flight
  userLoaded.value =
    false;

  venueReady.value =
    false;

  isOperations.value =
    false;

  userType.value =
    "single";

  isMultiVenue.value =
    false;

  allowedVenueIds.value =
    [];

  bankingSummaryPayload.value =
    null;

  const {
    ops,
    user_type,
    venue_id,
    venue_ids,
  } =
    await loadUserDoc(
      user.uid
    );

  isOperations.value =
    ops;

  userType.value =
    user_type;

  const isMulti =
    !ops &&
    user_type ===
      "multi";

  isMultiVenue.value =
    isMulti;

  allowedVenueIds.value =
    isMulti
      ? venue_ids
      : [];

  // Ops: do not force venue; use cached selection if any
  if (
    ops
  ) {
    const cached =
      asString(
        localStorage.getItem(
          LS_VENUE_A
        ) ||
          localStorage.getItem(
            LS_VENUE_B
          ) ||
          ""
      );

    venueId.value =
      cached;

    venueReady.value =
      !!venueId.value;

    startDayState(
      venueId.value
    );

    userLoaded.value =
      true;

    return;
  }

  // Multi: use cached selection if valid; else fallback
  if (
    isMulti
  ) {
    const cached =
      asString(
        localStorage.getItem(
          LS_VENUE_A
        ) ||
          localStorage.getItem(
            LS_VENUE_B
          ) ||
          ""
      );

    const fallback =
      venue_id ||
      allowedVenueIds.value[
        0
      ] ||
      "";

    const chosen =
      cached &&
      allowedVenueIds.value.includes(
        cached
      )
        ? cached
        : fallback;

    venueId.value =
      chosen;

    if (
      chosen
    ) {
      setActiveVenueStorage(
        chosen
      );
    }

    venueReady.value =
      !!venueId.value;

    startDayState(
      venueId.value
    );

    userLoaded.value =
      true;

    return;
  }

  // Single venue user/admin
  if (
    venue_id
  ) {
    venueId.value =
      venue_id;

    setActiveVenueStorage(
      venue_id
    );

    venueReady.value =
      true;

    startDayState(
      venueId.value
    );

    userLoaded.value =
      true;

    return;
  }

  venueId.value =
    "";

  venueReady.value =
    false;

  startDayState(
    ""
  );

  userLoaded.value =
    true;
}

/**
 * Prevent an Auth-state profile-read failure becoming an
 * uncaught Promise rejection.
 *
 * We deliberately do NOT invent a venue or grant access here.
 */
function logSyncError(
  source,
  user,
  error
) {
  console.error(
    `[useVenueContext] ${source} failed:`,
    {
      uid:
        user?.uid ||
        "",

      email:
        user?.email ||
        "",

      authenticatedUid:
        auth.currentUser
          ?.uid ||
        "",

      code:
        error?.code ||
        "",

      message:
        error?.message ||
        "",

      raw:
        error,
    }
  );
}

/*
 * Initial persisted-authentication load.
 */
(async () => {
  await authReady;

  if (
    auth.currentUser
  ) {
    try {
      await syncUser(
        auth.currentUser
      );
    } catch (error) {
      logSyncError(
        "initial user sync",
        auth.currentUser,
        error
      );
    }
  }
})();

/*
 * Subsequent authentication changes.
 */
onAuthStateChanged(
  auth,

  async (
    user
  ) => {
    if (
      !user
    ) {
      stopDayState();

      venueId.value =
        "";

      venueReady.value =
        false;

      isOperations.value =
        false;

      userType.value =
        "single";

      isMultiVenue.value =
        false;

      allowedVenueIds.value =
        [];

      tradingDate.value =
        "";

      weekEndingBase.value =
        "";

      isOpen.value =
        false;

      weekStartPending.value =
        false;

      bankingSummaryPayload.value =
        null;

      // ✅ NEW: make sure this is stable on logout
      userLoaded.value =
        false;

      // Clear view override on logout
      clearViewWeekEndingOverride();

      return;
    }

    try {
      await syncUser(
        user
      );
    } catch (error) {
      logSyncError(
        "auth-state user sync",
        user,
        error
      );
    }
  }
);

// Keep day_state synced with venue changes
watch(
  venueId,

  (
    v
  ) =>
    startDayState(
      v
    ),

  {
    immediate:
      true,
  }
);

export function useVenueContext() {
  return {
    venueId,
    tradingDate,

    // ✅ OVERRIDE-AWARE week ending
    weekEnding,

    isOpen,
    weekStartPending,

    venueReady,

    // ✅ NEW: stable ref expected across the app
    userLoaded,

    isOperations,

    userType,
    isMultiVenue,
    allowedVenueIds,

    // ✅ Banking snapshot payload store
    bankingSummaryPayload,
    setBankingSummaryPayload,
  };
}

export function waitForVenueReady() {
  return new Promise(
    (
      resolve
    ) => {
      if (
        venueReady.value
      ) {
        return resolve(
          true
        );
      }

      const stop =
        watch(
          venueReady,

          (
            v
          ) => {
            if (
              v
            ) {
              stop();

              resolve(
                true
              );
            }
          },

          {
            immediate:
              true,
          }
        );
    }
  );
}